import { useCallback, useEffect, useRef } from 'react';

import { differenceInMilliseconds, isToday } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useLocation } from 'react-router-dom';

import { selectDateAtom } from '../../modules/patientList/atoms';
import routesConstants from '../../routes/routes.constants';
import { mixpanelEvents, useTrackEvent } from '../mixpanel';

export const useTrackPageView = () => {
  const location = useLocation();
  const { trackEvent } = useTrackEvent();

  const selectedDate = useAtomValue(selectDateAtom);
  const previousRouteKey = useRef<string | null>(null);
  const pageViewStart = useRef<Date>(new Date());

  const handleTrackPageView = useCallback(() => {
    if (!previousRouteKey.current) return;

    const duration = pageViewStart.current
      ? differenceInMilliseconds(new Date(), pageViewStart.current) / 1000
      : undefined;
    trackEvent(mixpanelEvents.pageView, {
      page: previousRouteKey.current,
      duration,
      date: selectedDate,
      isToday: selectedDate ? isToday(selectedDate) : false,
    });
  }, [trackEvent, selectedDate]);

  useEffect(() => {
    const newRouteKey = Object.entries(routesConstants).find(([, path]) =>
      location.pathname.includes(path),
    )?.[0];

    // Don't track page view if the route is the same
    if (!newRouteKey || previousRouteKey.current === newRouteKey) {
      return;
    }
    // There won't be a previous route on first load
    if (!previousRouteKey.current) {
      previousRouteKey.current = newRouteKey;
      return;
    }

    handleTrackPageView();
    pageViewStart.current = new Date();
    previousRouteKey.current = newRouteKey;
  }, [handleTrackPageView, location.pathname]);

  useEffect(() => {
    // Track page view when the user exits the tab/window
    window.addEventListener('beforeunload', handleTrackPageView);

    return () => {
      window.removeEventListener('beforeunload', handleTrackPageView);
    };
  }, [handleTrackPageView]);
};
